import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, ExternalLink, Clock, User, Users, Sparkles, AlertTriangle,
  FolderOpen, RefreshCw, Link as LinkIcon, MessageSquare, ListChecks, Activity,
} from "lucide-react";

const LINK_STATUS = {
  linked: { label: "Linked", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  suggested_match: { label: "Suggested Match", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  unlinked: { label: "Unlinked", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  ignored: { label: "Ignored", tone: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}

export default function FathomCallDetail() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [progSearch, setProgSearch] = useState("");
  const [programs, setPrograms] = useState([]);
  const [reps, setReps] = useState([]);
  const [reprocessing, setReprocessing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/fathom/calls/${callId}`);
      setCall(r.data);
    } catch (e) {
      toast.error("Failed to load call");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [callId]);
  useEffect(() => { (async () => {
    try {
      const r = await apiClient.get("/programs");
      setPrograms(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { /* */ }
    try {
      const u = await apiClient.get("/users?role=Sales");
      setReps(Array.isArray(u.data) ? u.data : (u.data?.items || []));
    } catch { /* */ }
  })(); }, []);

  const linkToProgram = async (program_id) => {
    try {
      const r = await apiClient.post(`/fathom/calls/${callId}/link`, { program_id, status: "linked" });
      setCall(r.data);
      toast.success("Call linked to project");
      setLinkOpen(false);
    } catch (e) { toast.error("Link failed"); }
  };

  const unlink = async () => {
    try {
      const r = await apiClient.post(`/fathom/calls/${callId}/link`, { status: "unlinked" });
      setCall(r.data);
      toast.success("Call unlinked");
    } catch { toast.error("Unlink failed"); }
  };

  const markIgnored = async () => {
    try {
      const r = await apiClient.post(`/fathom/calls/${callId}/link`, { status: "ignored" });
      setCall(r.data);
      toast.success("Call ignored");
    } catch { toast.error("Failed"); }
  };

  const assignRep = async (sales_rep_id) => {
    try {
      const r = await apiClient.post(`/fathom/calls/${callId}/assign-rep`, { sales_rep_id: sales_rep_id || null });
      setCall(r.data);
      toast.success("Rep assigned");
    } catch { toast.error("Assign failed"); }
  };

  const reprocessAI = async () => {
    setReprocessing(true);
    try {
      await apiClient.post(`/fathom/calls/${callId}/reprocess-ai`);
      await load();
      toast.success("AI extraction refreshed");
    } catch { toast.error("Reprocess failed"); }
    finally { setReprocessing(false); }
  };

  if (loading || !call) {
    return <div className="p-6 text-slate-500">Loading…</div>;
  }

  const ai = call.ai_extraction || {};
  const isAiError = ai && (ai.error || ai.skipped);
  const filteredPrograms = programs.filter(p =>
    !progSearch || (p.program_name || "").toLowerCase().includes(progSearch.toLowerCase())
    || (p.client_name || "").toLowerCase().includes(progSearch.toLowerCase())
  ).slice(0, 50);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="fathom-call-detail">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/fathom/calls")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-5 gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Fathom Call</span>
          </div>
          <h1 className="text-2xl font-light text-slate-900 tracking-tight">{call.meeting_title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-2">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(call.meeting_at)}</span>
            <span>•</span>
            <span>{Math.round(call.duration_minutes || 0)}m</span>
            <span>•</span>
            <Badge variant="outline" className={`${LINK_STATUS[call.link_status]?.tone || ""} text-[10px]`}>{LINK_STATUS[call.link_status]?.label || call.link_status}</Badge>
            {call.program_name && (
              <Link to={`/programs/${call.program_id}`} className="flex items-center gap-1 text-emerald-700 hover:underline" data-testid="call-program-link">
                <FolderOpen className="w-3 h-3" /> {call.program_name}
              </Link>
            )}
            {call.risk_flag_count > 0 && (
              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                <AlertTriangle className="w-3 h-3 mr-1" /> {call.risk_flag_count} risk flags
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          {call.recording_url && (
            <a href={call.recording_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" data-testid="open-fathom-recording-btn"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in Fathom</Button>
            </a>
          )}
          <Button variant="outline" size="sm" onClick={() => setLinkOpen(true)} data-testid="link-project-btn">
            <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> {call.program_id ? "Change link" : "Link to project"}
          </Button>
          {call.program_id && <Button variant="ghost" size="sm" onClick={unlink}>Unlink</Button>}
          <Button variant="ghost" size="sm" onClick={markIgnored}>Ignore</Button>
          <Button variant="ghost" size="sm" onClick={reprocessAI} disabled={reprocessing} data-testid="reprocess-ai-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${reprocessing ? "animate-spin" : ""}`} /> Re-run AI
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* LEFT — main content */}
        <div className="col-span-2 space-y-4">
          {/* AI extraction — promoted to the top so reps see structured intelligence first */}
          {!isAiError && (
            <Card className="p-5" data-testid="call-ai-extraction-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-600" />
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">AI Intelligence</span>
              </div>
              <Tabs defaultValue="intelligence">
                <TabsList>
                  <TabsTrigger value="intelligence">Intelligence</TabsTrigger>
                  <TabsTrigger value="commitments">Commitments</TabsTrigger>
                  <TabsTrigger value="risks">Risk Flags{(ai.risk_flags || []).length > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] bg-rose-50 text-rose-700 border-rose-200">{ai.risk_flags.length}</Badge>}</TabsTrigger>
                  <TabsTrigger value="transcript">Transcript</TabsTrigger>
                </TabsList>
                <TabsContent value="intelligence" className="space-y-4 mt-4">
                  <AiSection label="Scope discussed" items={ai.scope_discussed} />
                  <AiSection label="Deliverables discussed" items={ai.deliverables_discussed} />
                  <AiSection label="Pricing mentions" items={ai.pricing_discussed} />
                  <AiSection label="Timeline expectations" items={ai.timeline_discussed} />
                  <AiSection label="Objections & concerns" items={ai.objections_concerns} />
                  <AiSection label="Decision makers" items={ai.decision_makers} />
                  <AiSection label="Upsell opportunities" items={ai.upsell_opportunities} accent="violet" />
                  <AiSection label="Next steps" items={ai.next_steps} />
                </TabsContent>
                <TabsContent value="commitments" className="space-y-4 mt-4">
                  <AiSection label="TRP commitments" items={ai.trp_commitments} accent="amber" />
                  <AiSection label="Client commitments" items={ai.client_commitments} />
                  <AiSection label="Action items" items={ai.action_items} />
                </TabsContent>
                <TabsContent value="risks" className="mt-4">
                  {(ai.risk_flags || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No risks flagged.</p>
                  ) : (
                    <ul className="space-y-2">
                      {(ai.risk_flags || []).map((r, i) => (
                        <li key={i} className="p-3 rounded border border-rose-100 bg-rose-50/40">
                          <div className="text-[10px] uppercase tracking-[0.14em] text-rose-700 font-semibold mb-1">{r.type?.replaceAll("_", " ")}</div>
                          <div className="text-sm text-slate-700">{r.note}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
                <TabsContent value="transcript" className="mt-4">
                  {(call.transcript || []).length === 0 ? (
                    <p className="text-sm text-slate-500">Transcript not included in webhook payload. <Link to="/fathom/settings" className="text-blue-600 underline">Update webhook config</Link>.</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                      {call.transcript.map((seg, i) => (
                        <div key={i} className="flex gap-3 p-2 rounded hover:bg-slate-50">
                          <div className="flex-shrink-0 w-[140px] text-[11px] text-slate-400">
                            {seg.speaker?.display_name || "Speaker"}
                            {seg.timestamp && <div className="text-[10px] text-slate-300 tabular-nums">{seg.timestamp}</div>}
                          </div>
                          <div className="text-sm text-slate-700 flex-1">{seg.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </Card>
          )}

          {call.summary && (
            <Card className="p-5" data-testid="call-summary-card">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Summary</div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{call.summary}</p>
              {call.key_takeaways?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Key takeaways</div>
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {call.key_takeaways.map((k, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="w-1 h-1 mt-2 rounded-full bg-slate-400 flex-shrink-0"></span>{typeof k === "string" ? k : (k.text || JSON.stringify(k))}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          {call.action_items?.length > 0 && (
            <Card className="p-5" data-testid="call-action-items-card">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="w-4 h-4 text-slate-500" />
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Action items</span>
                <Badge variant="outline" className="text-[10px]">{call.action_items.length}</Badge>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                {call.action_items.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 p-2 rounded bg-slate-50/60 border border-slate-100">
                    <span className="w-4 h-4 mt-0.5 rounded border border-slate-300 flex-shrink-0"></span>
                    <span>{typeof a === "string" ? a : (a.text || a.description || JSON.stringify(a))}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* RIGHT — meta */}
        <div className="col-span-1 space-y-4">
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Sales rep</div>
            <Select value={call.sales_rep_id || "_unassigned"} onValueChange={(v) => assignRep(v === "_unassigned" ? null : v)}>
              <SelectTrigger data-testid="call-rep-select">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_unassigned">Unassigned</SelectItem>
                {reps.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
              </SelectContent>
            </Select>
            {call.host_email && <div className="text-xs text-slate-500 mt-2">From: <span className="text-slate-700">{call.host_email}</span></div>}
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Participants</span>
              <Badge variant="outline" className="text-[10px]">{(call.participants || []).length}</Badge>
            </div>
            <ul className="space-y-1.5 text-sm">
              {(call.participants || []).map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-600">
                    {(p.name || p.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="text-slate-700 truncate">{p.name || p.email}</div>
                    {p.email && p.name && <div className="text-[11px] text-slate-400 truncate">{p.email}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {ai.client_company && (
            <Card className="p-4 bg-violet-50/40 border-violet-100">
              <div className="text-[10px] uppercase tracking-[0.16em] text-violet-700 mb-2">AI: Inferred client</div>
              <div className="text-sm text-slate-900 font-medium">{ai.client_company}</div>
              {ai.project_name && <div className="text-xs text-slate-600 mt-0.5">{ai.project_name}</div>}
              {ai.project_address && <div className="text-xs text-slate-500 mt-0.5">{ai.project_address}</div>}
            </Card>
          )}

          {call.link_status === "suggested_match" && (call.suggested_matches || []).length > 0 && (
            <Card className="p-4 bg-amber-50/40 border-amber-200">
              <div className="text-[10px] uppercase tracking-[0.16em] text-amber-800 mb-2">Suggested matches</div>
              <ul className="space-y-2">
                {call.suggested_matches.slice(0, 5).map((m, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-slate-800 truncate">{m.program_name}</div>
                      <div className="text-[11px] text-slate-500">{m.reason} · {(m.confidence * 100).toFixed(0)}%</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => linkToProgram(m.program_id)} data-testid={`suggested-link-${i}`}>Link</Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      {/* Link modal */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg" data-testid="link-project-modal">
          <DialogHeader>
            <DialogTitle>Link call to project</DialogTitle>
            <DialogDescription>Select the HQ project this call belongs to.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Search projects..." value={progSearch} onChange={(e) => setProgSearch(e.target.value)} className="mb-2" data-testid="link-modal-search" />
          <div className="max-h-[400px] overflow-y-auto border rounded">
            {filteredPrograms.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">No projects match.</div>
            ) : filteredPrograms.map(p => (
              <button
                key={p.id}
                onClick={() => linkToProgram(p.id)}
                className="w-full text-left p-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
                data-testid={`link-modal-program-${p.id}`}
              >
                <div className="text-sm font-medium text-slate-800">{p.program_name}</div>
                <div className="text-xs text-slate-500">{p.client_name || "—"}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AiSection({ label, items, accent }) {
  if (!items || (Array.isArray(items) && items.length === 0)) return null;
  const arr = Array.isArray(items) ? items : [items];
  const accentClasses = {
    amber: "bg-amber-50/50 border-amber-100",
    violet: "bg-violet-50/40 border-violet-100",
  };
  return (
    <div className={`p-3 rounded border ${accentClasses[accent] || "border-slate-100"}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-medium mb-1.5">{label}</div>
      <ul className="space-y-1 text-sm text-slate-700">
        {arr.map((x, i) => (
          <li key={i} className="flex items-start gap-2"><span className="w-1 h-1 mt-2 rounded-full bg-slate-400 flex-shrink-0"></span>{typeof x === "string" ? x : (x.text || x.note || JSON.stringify(x))}</li>
        ))}
      </ul>
    </div>
  );
}
